import { put, select, takeLatest, all } from 'redux-saga/effects';
import { notify } from '@mrlannigan/reapop';
import { sagaError } from 'helpers/handleGenericSagaError';
import { throttle } from 'helpers/sagaEffects';

import apiAuthCall from 'helpers/apiAuthCall';
import { get } from 'lodash';
import { clearAuthToken } from 'services/auth/duck';
import { push } from 'connected-react-router';
import { parseLocationUrl } from 'services/router/selectors';
import {
    hideGGBranding,
    showGGBranding,
    setOrganizationBrandingCode,
    updateThemeStyles
} from 'services/theme/duck';
import {
    createUserForm,
    listFetched,
    actions,
    fetchCurrentUserSuccess,
    fetchUserSuccess,
    submitUserForm
} from './duck';
import {
    newUserEndpoint,
    listUsersEndpoint,
    currentUserEndpoint,
    getUserEndpoint,
    updateUserEndpoint,
    resetUserPasswordEndpoint,
    sendUserDebugEndpoint
} from './selectors';


export function* createNewUser(api, action) {
    const notificationId = 'createUser';

    try {
        const payload = Object.assign({}, action.payload);

        if (payload.fieldEntities) {
            payload.entityIds = payload.fieldEntities.map(id => id);
            delete payload.fieldEntities;
        }

        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'post',
                url: yield select(newUserEndpoint),
                data: payload
            }
        );

        yield put(createUserForm.success());
        yield put(notify({
            id: notificationId,
            message: 'User Created!',
            status: result.status
        }));
        yield put(push(`/admin/users/${result.data.id}`));
    } catch (err) {
        yield put(createUserForm.failure(err));
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* fetchUsersList(api, action) {
    const notificationId = 'getUsersList';

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(listUsersEndpoint)
            }
        );

        yield put(listFetched(result.data));
    } catch (err) {
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* fetchCurrentUser(api, action) {
    const notificationId = action.type;

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(currentUserEndpoint)
            }
        );
        const wlHideGGBranding = get(result.data, 'brand.meta.whitelabel.hideGGBranding', false);
        const organizationBrandingCode = get(result.data, 'meta.organizationBrandingCode');

        const location = yield select(parseLocationUrl);

        /*
            This is lovecraftian tech debt. The first conditional checks to see
            if the user is whitelabeled. If they are, we disable the
            GG logo so there's no chance they will see it. The second conditional
            prevents the GG logo from flashing on an entity attached to a brand
            with custom branding. On a dashboard, the entity meta call will take care
            of determining the appropriate logo. On the admin, there is no entity meta call
            and so we want this saga to push the appropriate logo through showGGBranding.
        */
        if (wlHideGGBranding) {
            yield put(hideGGBranding());
        } else if (!location.pathname.startsWith('/dashboard/')) {
            yield put(showGGBranding());
        }

        yield put(updateThemeStyles(result.data));

        yield put(setOrganizationBrandingCode(organizationBrandingCode));

        yield put(fetchCurrentUserSuccess(result.data));


        if (!action.payload || !action.payload.disableNavigation) {
            yield put(push(result.data.landingPage));
        }
    } catch (err) {
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* destroyCurrentUserSession(api, action) {
    const notificationId = action.type;

    try {
        yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'delete',
                url: yield select(currentUserEndpoint)
            }
        );

        yield put(clearAuthToken());
    } catch (err) {
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* fetchUser(api, action) {
    const notificationId = action.type;

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(getUserEndpoint, action.payload)
            }
        );

        const { data } = result;

        data.fieldEntities = (data.Entities || []).filter((entity) => {
            if (entity.EntityAccessMap) {
                return entity.EntityAccessMap.explicit !== false;
            }

            return true;
        }).map(item => item.id);

        yield put(fetchUserSuccess(data));
    } catch (err) {
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* updateUser(api, action) {
    const notificationId = 'updateUser';

    try {
        const { payload } = action;

        if (payload.fieldEntities) {
            payload.Entities = payload.fieldEntities.map(id => ({ id }));
        }

        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'put',
                url: yield select(updateUserEndpoint, action.payload),
                data: action.payload
            }
        );

        yield put(submitUserForm.success());
        yield put(notify({
            id: notificationId,
            message: 'User updated!',
            status: result.status
        }));
        yield put(push(`/admin/users/${action.payload.id}/edit`));
    } catch (err) {
        yield put(submitUserForm.failure(err));
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}


export function* resetPassword(api, action) {
    const notificationId = 'resetPassword';

    try {
        const { user } = action.payload;

        let questioning = 0;
        let successMessage = 'Invitation email successfully sent!';
        if (user.lastLoginAt) {
            successMessage = 'Password reset email sent!';

            if (user.resetState === 'pending-reset') {
                questioning += confirm(`A pending reset already exists for ${user.fullName}, are you sure you wish to reset their password?`) ? 0 : 1; // eslint-disable-line no-alert
            } else {
                questioning += confirm(`Reset ${user.fullName}'s password?`) ? 0 : 1; // eslint-disable-line no-alert
            }
        } else if (user.resetState === 'pending-invite') {
            questioning += confirm(`A pending invite already exists for ${user.fullName}, are you sure you wish to send them another invite, invalidating their current invite?`) ? 0 : 1; // eslint-disable-line no-alert
        } else {
            questioning += confirm(`Send ${user.fullName} an invitation email?`) ? 0 : 1; // eslint-disable-line no-alert
        }

        if (questioning === 0) { // eslint-disable-line no-alert
            const result = yield apiAuthCall(
                action.getState(),
                [api, api.request],
                {
                    method: 'post',
                    url: yield select(resetUserPasswordEndpoint, user),
                    data: action.payload
                }
            );

            yield put(notify({
                id: notificationId,
                message: successMessage,
                status: result.status
            }));
            yield put(push('/admin/users'));
        }
    } catch (err) {
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export function* sendUserDebug(api, action) {
    const notificationId = 'sendUserDebug';

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'post',
                url: yield select(sendUserDebugEndpoint),
                data: action.payload
            }
        );

        yield put(notify({
            id: notificationId,
            message: 'Debug info has been sent. Thank you!',
            status: result.status
        }));
    } catch (err) {
        yield put(notify({
            id: notificationId,
            ...sagaError(err)
        }));
    }
}

export default function* usersSaga({ api }) {
    yield all([
        takeLatest(createUserForm.REQUEST, createNewUser, api),
        takeLatest(actions.FETCH_LIST, fetchUsersList, api),
        throttle(15000, actions.FETCH_CURRENT_USER, fetchCurrentUser, api),
        throttle(1000, actions.FETCH_USER, fetchUser, api),
        takeLatest(submitUserForm.REQUEST, updateUser, api),
        takeLatest(actions.RESET_PASSWORD, resetPassword, api),
        takeLatest(actions.DESTROY_CURRENT_USER_SESSION, destroyCurrentUserSession, api),
        // Only allow one Debug message every 10 seconds
        throttle(10000, actions.SEND_DEBUG, sendUserDebug, api)
    ]);
}



// WEBPACK FOOTER //
// ./src/services/users/saga.js